import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Search } from "lucide-react";
import client, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crest, Card, Pill, Zig, Loading } from "../components/common";
import { AdSlot } from "../components/AdSlot";

const NEED = { GK: 2, DEF: 5, MID: 5, FWD: 3 };
const PITCH = "https://customer-assets-v7afamib.emergentagent.net/job_dynamos-portal/artifacts/9ljrk7in_772119602_1374196178247998_4845697400721189189_n.png.jpeg";

export default function FantasyPick() {
  const { user } = useAuth();
  const [players, setPlayers] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [picked, setPicked] = useState([]);
  const [teamName, setTeamName] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useState("");

  useEffect(() => {
    client.get("/players").then((r) => setPlayers(r.data));
    client.get("/fantasy/config").then((r) => setCfg(r.data));
    client.get("/fantasy/squad").then((r) => { if (r.data?.player_ids) { setPicked(r.data.player_ids); setTeamName(r.data.team_name || ""); } }).catch(() => {});
  }, []);

  const byId = useMemo(() => Object.fromEntries((players || []).map((p) => [p.id, p])), [players]);
  const pickedPlayers = picked.map((id) => byId[id]).filter(Boolean);
  const spent = pickedPlayers.reduce((a, p) => a + p.zig_price, 0);
  const posCount = pickedPlayers.reduce((a, p) => { a[p.position] = (a[p.position] || 0) + 1; return a; }, {});
  const clubCount = pickedPlayers.reduce((a, p) => { a[p.club_slug] = (a[p.club_slug] || 0) + 1; return a; }, {});
  const budget = cfg?.budget || 100;
  const remaining = budget - spent;

  if (user === null || players === null || !cfg) return <Loading />;
  if (!user) return <Navigate to="/login" />;

  const canAdd = (p) => {
    if (picked.includes(p.id)) return true;
    if (picked.length >= 15) return false;
    if ((posCount[p.position] || 0) >= NEED[p.position]) return false;
    if ((clubCount[p.club_slug] || 0) >= 3) return false;
    if (p.zig_price > remaining) return false;
    return true;
  };
  const toggle = (p) => {
    if (picked.includes(p.id)) setPicked(picked.filter((id) => id !== p.id));
    else if (canAdd(p)) setPicked([...picked, p.id]);
    else toast.error("Cannot add: budget, formation (2-5-5-3) or 3-per-club limit reached");
  };
  const save = async () => {
    try { const { data } = await client.post("/fantasy/squad", { player_ids: picked, team_name: teamName || undefined }); toast.success(`Squad saved · cost ${data.cost} · ${data.points} pts`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const list = players.filter((p) => (filter === "ALL" || p.position === filter) && p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="fantasy-pick-page">
      <AdSlot id="fantasy.picker" height={80} className="mb-5" />
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Pitch + summary */}
        <div className="lg:col-span-3">
          <Card className="p-4 mb-4">
            <div className="flex items-center gap-4">
              <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team name" className="flex-1 border border-pslborder rounded-sm p-2.5 text-sm font-semibold focus:outline-none focus:border-gold" data-testid="team-name-input" />
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-ink/50">Remaining</div>
                <div className={`font-head font-black text-xl tabular ${remaining < 0 ? "text-live" : "text-ink"}`}>ZiG {remaining.toFixed(1)}m</div>
              </div>
            </div>
            <div className="flex gap-3 mt-3 text-xs">
              {Object.keys(NEED).map((pos) => <span key={pos} className={`px-2 py-1 rounded-sm font-bold ${(posCount[pos] || 0) === NEED[pos] ? "bg-green text-cream" : "bg-cream border border-pslborder"}`}>{pos} {posCount[pos] || 0}/{NEED[pos]}</span>)}
              <span className="px-2 py-1 rounded-sm bg-cream border border-pslborder ml-auto font-bold">{picked.length}/15</span>
            </div>
          </Card>
          <div className="relative rounded-sm overflow-hidden" style={{ aspectRatio: "3/4", maxHeight: 560 }}>
            <img src={PITCH} alt="Fantasy football pitch" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-green/40" />
            <div className="relative h-full flex flex-col justify-around py-4">
              {["GK", "DEF", "MID", "FWD"].map((pos) => (
                <div key={pos} className="flex justify-center gap-2 flex-wrap px-2">
                  {Array.from({ length: NEED[pos] }).map((_, i) => {
                    const p = pickedPlayers.filter((x) => x.position === pos)[i];
                    return (
                      <div key={i} className="w-16 text-center" data-testid={p ? `pitch-${p.id}` : `pitch-empty-${pos}-${i}`}>
                        <div className="mx-auto w-10 h-10 rounded-full bg-white/90 border-2 border-gold flex items-center justify-center">
                          {p ? <Crest slug={p.club_slug} name={p.club_slug} size={30} /> : <span className="text-ink/30 text-xs">{pos}</span>}
                        </div>
                        {p && <div className="mt-1 bg-ink/80 text-cream text-[10px] rounded-sm px-1 truncate">{p.name.split(" ").pop()}</div>}
                        {p && <div className="text-[10px] text-gold font-bold tabular">{p.zig_price.toFixed(1)}</div>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={picked.length !== 15} data-testid="save-squad-btn" className="w-full mt-4 py-3 bg-gold text-ink font-bold rounded-sm disabled:opacity-40 hover:bg-green hover:text-cream transition-colors">Save squad ({picked.length}/15)</button>
        </div>

        {/* Player list */}
        <div className="lg:col-span-2">
          <div className="flex gap-2 mb-3">
            {["ALL", "GK", "DEF", "MID", "FWD"].map((f) => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-sm text-xs font-bold ${filter === f ? "bg-green text-cream" : "bg-cream border border-pslborder"}`}>{f}</button>)}
          </div>
          <div className="relative mb-3"><Search size={16} className="absolute left-3 top-3 text-ink/40" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players" className="w-full border border-pslborder rounded-sm p-2.5 pl-9 text-sm focus:outline-none focus:border-gold" data-testid="player-search" /></div>
          <Card className="divide-y divide-pslborder max-h-[560px] overflow-y-auto scrollbar-thin">
            {list.map((p) => {
              const on = picked.includes(p.id);
              return (
                <button key={p.id} onClick={() => toggle(p)} data-testid={`pick-player-${p.id}`} className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-cream ${on ? "bg-green/5" : ""}`}>
                  <Crest slug={p.club_slug} name={p.club_slug} size={26} />
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{p.name}</div><div className="text-[11px] text-ink/50">{p.position} · {p.points} pts</div></div>
                  <Zig value={p.zig_price} className="text-sm font-bold text-green" />
                  <span className={`w-6 h-6 rounded-sm flex items-center justify-center ${on ? "bg-live text-white" : "bg-gold text-ink"}`}>{on ? <X size={14} /> : <Check size={14} />}</span>
                </button>
              );
            })}
          </Card>
        </div>
      </div>
    </div>
  );
}
